import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Calendar, Plus, Users, CalendarDays } from 'lucide-react';
import EventCard from '@/components/EventCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_REFRESH_INTERVAL = 5000;

// Status badge styles
const STATUS_BADGE = {
  'agendada': { label: 'Não Confirmada', badge: 'bg-amber-100 text-amber-700 border-amber-300' },
  'confirmada': { label: 'Confirmada', badge: 'bg-green-100 text-green-700 border-green-300' },
  'realizada': { label: 'Realizada', badge: 'bg-gray-100 text-gray-600 border-gray-300' },
  'cancelada': { label: 'Cancelada', badge: 'bg-red-100 text-red-700 border-red-300' },
  'falta': { label: 'Falta', badge: 'bg-amber-100 text-amber-700 border-amber-300' }
};

// Card colors based on tipo (pastel)
const getCardColor = (consulta) => {
  if (consulta.categoria === 'Horário Bloqueado' || consulta.paciente_nome === 'Horário Bloqueado') return '#9CA3AF';
  if (consulta.tipo_consulta === 'retorno' || consulta.tipo_cobranca === 'retorno_gratuito') return '#E879A0';
  if (consulta.modo_atendimento === 'online') return '#8B8FD6';
  return '#6BA3E8';
};

function DashboardPage({ user, onLogout, showConfig }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [consultasHoje, setConsultasHoje] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [procedimentoCores, setProcedimentoCores] = useState({});
  const [pagamentosResumo, setPagamentosResumo] = useState({});
  const previousDataRef = useRef(null);

  // Carrega procedimentos
  const loadProcedimentos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/procedimentos`);
      setProcedimentos(response.data);
      const cores = {};
      response.data.forEach(p => {
        cores[p.nome] = p.cor;
      });
      setProcedimentoCores(cores);
    } catch (error) {
      console.error('Erro ao carregar procedimentos:', error);
    }
  }, []);

  // Get today's date string
  const getTodayStr = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load consultas de hoje
  const loadConsultasHoje = useCallback(async (silent = false) => {
    try {
      const todayStr = getTodayStr();
      const response = await axios.get(`${API}/consultas?data=${todayStr}`);
      
      const newDataHash = JSON.stringify(response.data.map(c => ({ id: c.id, status: c.status })));
      
      if (previousDataRef.current !== newDataHash) {
        previousDataRef.current = newDataHash;
        
        // Filtrar apenas consultas não canceladas e ordenar por hora
        const filtered = response.data
          .filter(c => c.status !== 'cancelada')
          .sort((a, b) => (a.hora_inicio || '00:00').localeCompare(b.hora_inicio || '00:00'));
        
        setConsultasHoje(filtered);
      }
    } catch (error) {
      console.error('Erro ao carregar consultas:', error);
      if (!silent) toast.error('Erro ao carregar consultas');
    } finally {
      if (loading) setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    loadProcedimentos();
    loadConsultasHoje();
    // Carrega resumo de pagamentos (silent, fail-silent)
    axios.get(`${API}/pagamentos/resumo`).then(r => setPagamentosResumo(r.data || {})).catch(() => {});
    
    // Check if redirected after Google auth
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      toast.success('Google conectado com sucesso!');
      window.history.replaceState({}, document.title, '/');
    } else if (params.get('google_error')) {
      toast.error('Erro ao conectar Google: ' + params.get('google_error'));
      window.history.replaceState({}, document.title, '/');
    }
    
    const intervalId = setInterval(() => loadConsultasHoje(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [loadConsultasHoje, loadProcedimentos]);

  // Helper para obter cor do procedimento (com busca parcial)
  const getProcedimentoCor = (categoria) => {
    if (!categoria) return '#6B7280';
    
    // Check if blocked
    if (categoria === 'Horário Bloqueado') return '#6B7280';
    
    // Primeiro tenta match exato
    if (procedimentoCores[categoria]) return procedimentoCores[categoria];
    if (DEFAULT_CATEGORY_COLORS[categoria]) return DEFAULT_CATEGORY_COLORS[categoria];
    
    // Busca parcial
    const categoriaLower = categoria.toLowerCase();
    for (const [nome, cor] of Object.entries(procedimentoCores)) {
      const nomeLower = nome.toLowerCase();
      if (categoriaLower.includes(nomeLower) || nomeLower.includes(categoriaLower)) {
        return cor;
      }
    }
    
    for (const [nome, cor] of Object.entries(DEFAULT_CATEGORY_COLORS)) {
      const nomeLower = nome.toLowerCase();
      if (categoriaLower.includes(nomeLower) || nomeLower.includes(categoriaLower)) {
        return cor;
      }
    }
    
    return '#6B7280';
  };

  // Get day name
  const getDayName = () => {
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[new Date().getDay()];
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="space-y-6" data-testid="dashboard-container">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Consultas de Hoje</h1>
          <p className="text-gray-600 mt-1">Visão geral da clínica</p>
        </div>

        {/* Consultas do Dia - Card com scroll */}
        <Card className="p-4 sm:p-6">
          {/* Header do card */}
          <div className="mb-4">
            <p className="text-sm text-gray-500">{getDayName()}</p>
            <p className="text-4xl sm:text-5xl font-bold text-gray-900">{new Date().getDate()}</p>
          </div>

          {/* Lista de consultas com scroll */}
          <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto space-y-5 pl-1.5 pr-1.5 pt-2">
            {consultasHoje.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 text-gray-600">Nenhuma consulta agendada para hoje</p>
                <button
                  onClick={() => navigate('/nova-consulta')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="btn-agendar-primeira"
                >
                  Agendar Consulta
                </button>
              </div>
            ) : (
              consultasHoje.map((consulta) => (
                <div key={consulta.id} data-testid={`consulta-${consulta.id}`}>
                  <EventCard
                    consulta={consulta}
                    onClick={() => navigate(`/consulta/${consulta.id}`)}
                    totalPago={pagamentosResumo[consulta.id]}
                  />
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Ações Rápidas */}
        <Card className="p-4 sm:p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 sm:mb-6">Ações Rápidas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/nova-consulta')}
              className="p-4 sm:p-6 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
              data-testid="btn-nova-consulta"
            >
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mb-3">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-gray-900">Nova Consulta</h3>
              <p className="text-sm text-gray-600 mt-1">Agendar novo atendimento</p>
            </button>

            <button
              onClick={() => navigate('/pacientes')}
              className="p-4 sm:p-6 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left"
              data-testid="btn-gerenciar-pacientes"
            >
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-gray-900">Gerenciar Pacientes</h3>
              <p className="text-sm text-gray-600 mt-1">Adicionar ou editar pacientes</p>
            </button>

            <button
              onClick={() => navigate('/agenda')}
              className="p-4 sm:p-6 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-left"
              data-testid="btn-ver-agenda"
            >
              <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center mb-3">
                <CalendarDays className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-gray-900">Ver Agenda</h3>
              <p className="text-sm text-gray-600 mt-1">Consultar todos os agendamentos</p>
            </button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default DashboardPage;
